import {Trans} from '@lingui/macro';
import {isUndefined, pick} from 'lodash';
import React, {FC} from 'react';

import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {CalendarModel} from '../../../../models/api/calendarModel';
import {CalendarVersionModel} from '../../../../models/api/calendarVersionModel';
import {ResourceMap} from '../../../../store/appTypes';
import {getWriteAccessCalendarVersions} from '../../../../store/data/calendarVersions/calendarVersionsSelectors';
import {getCalendars} from '../../../../store/data/dataSelectors';
import {SettingsPreferenceItemInheritableProps} from '../../settingsPreferencesList';

/*
 * Constant.
 */

const defaultNullDisplayName = (
  <Trans id="teammate-prefs-default-calendar-null-name">No default calendar</Trans>
);

/*
 * Props.
 */

interface DefaultCalendarDropdownProps extends SettingsPreferenceItemInheritableProps<number | undefined> {
  currentCalendarVersionId: number | undefined;
}

/*
 * Component.
 */

export const DefaultCalendarDropdown: FC<DefaultCalendarDropdownProps> = (props) => {
  const {currentCalendarVersionId, updatePreference} = props;
  const writeAccessCalendarVersions = useAppSelector(getWriteAccessCalendarVersions);
  const writeAccessCalendarIds = writeAccessCalendarVersions.map(
    (calendarVersion) => calendarVersion.calendarId,
  );
  const currentCalendarVersion = writeAccessCalendarVersions.find((cv) => cv.id === currentCalendarVersionId);

  const calendars = useAppSelector(getCalendars);
  const calendarsWithWriteAccess = pick(calendars, writeAccessCalendarIds);

  const currentDisplayName = isUndefined(currentCalendarVersionId)
    ? defaultNullDisplayName
    : computeDisplayName(currentCalendarVersion, calendarsWithWriteAccess);

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <SimpleDropdown {...popoverProps}>
      <SimpleDropdownItem
        key="default-calendar-item-none"
        onSelect={() => updatePreference(undefined)}
        isChecked={isUndefined(currentCalendarVersionId)}
      >
        {defaultNullDisplayName}
      </SimpleDropdownItem>
      {writeAccessCalendarVersions.map((calendarVersion) => {
        const calendarVersionId = calendarVersion.id;
        return (
          <SimpleDropdownItem
            key={`default-calendar-item-${calendarVersionId}`}
            onSelect={() => updatePreference(calendarVersionId)}
            isChecked={currentCalendarVersionId === calendarVersionId}
          >
            {computeDisplayName(calendarVersion, calendarsWithWriteAccess)}
          </SimpleDropdownItem>
        );
      })}
    </SimpleDropdown>
  );

  return (
    <DropdownBoxCoordinator
      value={currentDisplayName ?? defaultNullDisplayName}
      renderDropdown={renderDropdown}
      noInputMarginRight
    />
  );
};

function computeDisplayName(
  calendarVersion: CalendarVersionModel | undefined,
  calendars: Pick<ResourceMap<CalendarModel>, number>,
) {
  if (!calendarVersion) {
    return defaultNullDisplayName;
  }

  return calendarVersion.nameOverride ?? calendars[calendarVersion.calendarId]?.name;
}
